import { normalizeVoiceText, parseVoiceDateTime, parseVoiceNumber } from "../../voice/ownerVoiceParsers";
import { chooseEntity, idOf, localYmd, spokenDuration } from "../ownerJarvisUtils";

const LIVE_TAG = "[day-live-block]";
const COMMITMENT_LINK_PREFIX = "[day-live-commitment:";
const ACTIVE_STATES = new Set(["ready", "in_progress"]);
const EXECUTABLE_KINDS = new Set(["task", "follow_up", "promise", "project_next_step"]);
const EXECUTION_CONTEXT_LABELS = Object.freeze({
  programming: "Programación",
  home: "Casa",
  administration: "Administración",
  calls: "Llamadas / mensajes",
  errands: "Trámites / calle",
  study: "Estudio",
  personal: "Personal",
  work: "Trabajo general",
  other: "Otro",
});
const START_HINT_RE = /\b(?:me puse a|me pongo a|me pongo con|me estoy|estoy|arranque a|arranque|empece a|empece|voy a empezar a|me sente a|me siento a)\b/;
const FINISH_HINT_RE = /\b(?:termine|ya termine|finalice|pare|corte|deje de|listo con|termine de|ya esta con)\b/;
const POINT_EVENT_RE = /\b(?:me levante|me desperte|me acabo de levantar|acabo de levantarme|desayune|almorce|cene|comi|me bane|sali|llegue|me acoste)\b/;
const TODAY_PENDING_RE = /\b(?:pendientes?|tareas?|acciones?)\b.*\b(?:hoy|para hacer|para avanzar)\b|\b(?:hoy)\b.*\b(?:pendientes?|tareas?|acciones?)\b/;
const ACTIVE_QUERY_RE = /\b(?:que estoy haciendo|que tengo corriendo|que esta corriendo|cuanto llevo|cuanto tiempo llevo)\b/;
const DAY_SELECTION_STORAGE_KEY = "wp-owner-day-pending-selection-until";
const COMPLETE_PROJECT_RE = /\b(?:complete|completado|termine|finalice|hecho)\b.*\b(?:pendiente|tarea|fix|accion)\b|\b(?:pendiente|tarea|fix|accion)\b.*\b(?:complet|termin|hech)\w*/;


function stripJarvis(raw = "") {
  return String(raw || "").replace(/^\s*jarvis[,:]?\s*/i, "").trim();
}

function hasExplicitClock(text = "") {
  return /\b(?:ahora|recien|reci[eé]n|acabo|a\s+las?|a\s+la|\d{1,2}:\d{2}|\d{1,2}\s+y\s+(?:media|cuarto))\b/i.test(String(text || ""));
}

function dateFromSpeech(raw, { fallbackNow = true } = {}) {
  const clean = stripJarvis(raw);
  if (/\b(?:ahora|recien|reci[eé]n|acabo)\b/i.test(clean)) return new Date();
  if (!hasExplicitClock(clean) && fallbackNow) return new Date();
  const local = parseVoiceDateTime(clean, new Date());
  const parsed = local ? new Date(local) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
}

function formatClock(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "ahora";
  return new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(date);
}

function titleCase(value = "") {
  const clean = String(value || "").trim().replace(/[.]+$/, "");
  return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : "Actividad";
}

function activityTitle(text = "") {
  const normalized = normalizeVoiceText(text);
  const known = [
    [/(?:cocinando|cocinar)/, "Cocinar"],
    [/(?:comiendo|comer)/, "Comer"],
    [/(?:leyendo|leer)/, "Leer"],
    [/(?:programando|programar|codeando|codear)/, "Programar"],
    [/(?:meditando|meditar)/, "Meditar"],
    [/(?:entrenando|entrenar|ejercicio)/, "Entrenar"],
    [/(?:trabajando|trabajar)/, "Trabajar"],
    [/(?:estudiando|estudiar)/, "Estudiar"],
    [/(?:manejando|manejar|conduciendo)/, "Manejar"],
    [/(?:caminando|caminar)/, "Caminar"],
  ];
  const matched = known.find(([pattern]) => pattern.test(normalized));
  if (matched) return matched[1];

  const tail = normalized
    .replace(/^.*?\b(?:me puse a|me pongo a|me pongo con|me estoy|estoy|arranque a|arranque|empece a|empece|voy a empezar a|me sente a|me siento a)\b\s*/, "")
    .replace(/\b(?:ahora|hoy)\b/g, "")
    .trim();
  if (!tail || /^(?:bien|mal|cansado|cansada|listo|lista|aca|aqui)$/.test(tail)) return "";
  return titleCase(tail.slice(0, 120));
}

function momentTitle(text = "") {
  const normalized = normalizeVoiceText(text);
  if (/\b(?:me (?:levante|desperte|acabo de levantar)|acabo de levantarme)\b/.test(normalized)) return "Me levanté";
  if (/\bdesayune\b/.test(normalized)) return "Desayuné";
  if (/\balmorce\b/.test(normalized)) return "Almorcé";
  if (/\bcene\b/.test(normalized)) return "Cené";
  if (/\bcomi\b/.test(normalized)) return "Comí";
  if (/\bme bane\b/.test(normalized)) return "Me bañé";
  if (/\bsali\b/.test(normalized)) return "Salí";
  if (/\bllegue\b/.test(normalized)) return "Llegué";
  if (/\bme acoste\b/.test(normalized)) return "Me acosté";
  return titleCase(text);
}

function linkedCommitmentId(session) {
  const notes = String(session?.notes || "");
  const current = notes.match(/\[day-live-commitment:([^\]]+)\]/);
  if (current?.[1]) return String(current[1]);
  const legacy = notes.match(/\[day-live-project-step:([^\]]+)\]/);
  return String(legacy?.[1] || "");
}

function executionContextOf(row) {
  return String(row?.work?.executionContext || "").trim();
}

function executionContextLabel(value) {
  return EXECUTION_CONTEXT_LABELS[String(value || "").trim()] || "";
}

function activityExecutionContext(title = "") {
  const value = normalizeVoiceText(title);
  if (/program|codear|codigo/.test(value)) return "programming";
  if (/casa|hogar/.test(value)) return "home";
  if (/admin|papeles|factur|cobro/.test(value)) return "administration";
  if (/llamad|mensaje|whatsapp|contact/.test(value)) return "calls";
  if (/tramit|calle|mandado/.test(value)) return "errands";
  if (/estudi|facultad|curso/.test(value)) return "study";
  if (/personal/.test(value)) return "personal";
  if (/trabaj/.test(value)) return "work";
  return "";
}

function isLiveSession(row) {
  return String(row?.status || "open") === "open"
    && String(row?.work?.state || "") === "in_progress"
    && String(row?.notes || "").includes(LIVE_TAG);
}

function sessionStartedAt(row) {
  return new Date(row?.scheduledStartAt || row?.work?.startedAt || row?.createdAt || Date.now());
}

function projectNameMap(initiatives = []) {
  return new Map((initiatives || []).map((row) => [idOf(row), String(row?.name || "Proyecto")]).filter(([id]) => Boolean(id)));
}

function isToday(value) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return localYmd(date) === localYmd();
}

function actionableCommitments(commitments = [], initiatives = []) {
  const byId = new Map((commitments || []).map((row) => [idOf(row), row]).filter(([id]) => Boolean(id)));
  const names = projectNameMap(initiatives);
  const priorityRank = { high: 0, normal: 1, low: 2 };

  return (commitments || [])
    .filter((row) => EXECUTABLE_KINDS.has(String(row?.kind || "task")) && String(row?.status || "open") === "open")
    .filter((row) => !isLiveSession(row))
    .filter((row) => ACTIVE_STATES.has(String(row?.work?.state || "ready")))
    .filter((row) => {
      if (row?.kind !== "project_next_step") return true;
      const parentId = idOf(row?.parentCommitmentId);
      if (!parentId) return true;
      const parent = byId.get(parentId);
      return !parent || String(parent?.status || "open") === "done";
    })
    .map((row) => {
      const initiativeName = names.get(idOf(row?.initiativeId)) || "";
      const contextKey = executionContextOf(row);
      const contextName = executionContextLabel(contextKey);
      return {
        ...row,
        id: idOf(row),
        projectName: initiativeName,
        contextKey,
        contextName,
        groupName: contextName || initiativeName || "General",
        scheduledToday: isToday(row?.scheduledStartAt),
        dueToday: isToday(row?.dueAt),
        label: [initiativeName, contextName, String(row?.title || "Pendiente")].filter(Boolean).join(" "),
      };
    })
    .sort((a, b) => {
      if (String(a?.work?.state) === "in_progress" && String(b?.work?.state) !== "in_progress") return -1;
      if (String(b?.work?.state) === "in_progress" && String(a?.work?.state) !== "in_progress") return 1;
      if (a.scheduledToday !== b.scheduledToday) return a.scheduledToday ? -1 : 1;
      if (a.dueToday !== b.dueToday) return a.dueToday ? -1 : 1;
      const priority = (priorityRank[a.priority] ?? 1) - (priorityRank[b.priority] ?? 1);
      if (priority) return priority;
      return String(a.groupName || "").localeCompare(String(b.groupName || ""), "es");
    });
}

function spokenTodayPendings(rows = [], { contextName = "" } = {}) {
  if (!rows.length) return contextName
    ? `No veo tareas tomadas para el bloque ${contextName}. Si querés, igual empiezo a contar ese bloque libre.`
    : "No veo tareas ejecutables para elegir ahora. Las notas que todavía están en Inbox no compiten por tu atención hasta que decidas tomarlas.";
  const visible = rows.slice(0, 15);
  const grouped = new Map();
  visible.forEach((row, index) => {
    const group = row.groupName || "General";
    if (!grouped.has(group)) grouped.set(group, []);
    const projectPrefix = row.projectName && row.projectName !== group ? `${row.projectName}, ` : "";
    grouped.get(group).push(`${index + 1}, ${projectPrefix}${row.title}${String(row?.work?.state) === "in_progress" ? ", ya en marcha" : ""}`);
  });
  const body = [...grouped.entries()].map(([group, items]) => `${group}: ${items.join("; ")}`).join(". ");
  const more = rows.length > visible.length ? ` Hay ${rows.length - visible.length} más; si querés después seguimos.` : "";
  const intro = contextName ? `Para ${contextName} tenés ${rows.length} tareas tomadas.` : `Para elegir ahora tengo ${rows.length} tareas ejecutables.`;
  return `${intro} ${body}.${more} Decime el número o nombrame la tarea y empiezo a contar el tiempo.`;
}


function markDaySelectionActive(active) {
  if (typeof window === "undefined") return;
  try {
    if (!active) window.sessionStorage.removeItem(DAY_SELECTION_STORAGE_KEY);
    else window.sessionStorage.setItem(DAY_SELECTION_STORAGE_KEY, String(Date.now() + 5 * 60 * 1000));
  } catch { /* optional */ }
}

function dispatchMutation() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("owner:jarvis-mutated", { detail: { scopes: ["today", "calendar", "commitments", "projects"] } }));
  } catch { /* noop */ }
}

async function findActiveSession(ownerApi) {
  const response = await ownerApi.listCommitments({ status: "open", limit: 200 });
  const sessions = (response?.commitments || []).filter(isLiveSession).sort((a, b) => sessionStartedAt(b) - sessionStartedAt(a));
  return { session: sessions[0] || null, commitments: response?.commitments || [] };
}

async function closeSession(ownerApi, session, {
  finishAt = new Date(),
  completeProjectStep = false,
  commitments = null,
} = {}) {
  if (!session) return null;
  const startAt = sessionStartedAt(session);
  const endAt = finishAt instanceof Date ? finishAt : new Date(finishAt);
  const durationMs = endAt.getTime() - startAt.getTime();
  if (!Number.isFinite(durationMs) || durationMs < 0) {
    return { error: `Ese cierre queda antes del inicio de ${session.title}. Decime una hora posterior a ${formatClock(startAt)}.` };
  }
  const minutes = Math.max(1, Math.min(1440, Math.round(durationMs / 60000)));
  const linkedId = linkedCommitmentId(session);
  const allCommitments = commitments || (await ownerApi.listCommitments({ status: "open", limit: 200 }))?.commitments || [];
  const linked = linkedId ? allCommitments.find((row) => idOf(row) === linkedId) : null;
  const timeTargetId = linkedId || idOf(session);

  await ownerApi.addCommitmentTime(timeTargetId, {
    minutes,
    occurredAt: startAt.toISOString(),
    note: linkedId ? "Sesión real registrada desde Hoy." : `Bloque real: ${session.title}`,
    source: { kind: "voice", externalKey: `day-live-time:${idOf(session)}` },
  });

  if (linked) {
    if (completeProjectStep) {
      await ownerApi.updateCommitment(linkedId, { status: "done" });
    } else {
      await ownerApi.updateCommitment(linkedId, { work: { ...(linked.work || {}), state: "ready" } });
    }
  }
  await ownerApi.updateCommitment(idOf(session), { status: "done" });
  dispatchMutation();
  return { session, linked, minutes, startAt, endAt, completeProjectStep };
}

async function startSession(ownerApi, {
  title,
  startAt = new Date(),
  linkedCommitment = null,
  projectName = "",
  executionContext = "",
} = {}) {
  const active = await findActiveSession(ownerApi);
  let previous = null;
  if (active.session) previous = await closeSession(ownerApi, active.session, { finishAt: startAt, commitments: active.commitments });
  if (previous?.error) return { error: previous.error, previous };

  if (linkedCommitment) {
    await ownerApi.updateCommitment(idOf(linkedCommitment), { work: { ...(linkedCommitment.work || {}), state: "in_progress" } });
  }

  const displayTitle = linkedCommitment
    ? `${projectName ? `${projectName} · ` : ""}${linkedCommitment.title}`
    : title;
  const response = await ownerApi.createCommitment({
    title: displayTitle,
    kind: "task",
    priority: "normal",
    scheduledStartAt: startAt.toISOString(),
    // La sesión viva es infraestructura del día, no otro pendiente del proyecto.
    // El tiempo real se imputa al pending original al cerrar el bloque.
    initiativeId: null,
    work: { state: "in_progress", ...(executionContext ? { executionContext } : {}) },
    notes: `${LIVE_TAG}${linkedCommitment ? `\n${COMMITMENT_LINK_PREFIX}${idOf(linkedCommitment)}]` : ""}`,
    source: { kind: "voice", externalKey: `day-live:${Date.now()}:${Math.random().toString(36).slice(2, 8)}` },
  });
  dispatchMutation();
  return { session: response?.commitment, previous };
}

function selectionFromSpeech(raw, candidates = []) {
  const number = Math.round(Number(parseVoiceNumber(raw)));
  if (Number.isFinite(number) && number >= 1 && number <= candidates.length) return candidates[number - 1];
  const text = normalizeVoiceText(raw).replace(/\b(?:me interesa|quiero|el|la|pendiente|tarea|numero|nro|me pongo con|arranco con|empiezo con)\b/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return null;
  const result = chooseEntity(candidates, text, ["label", "title", "projectName"]);
  return result.row || null;
}

function isSelectionUtterance(text = "") {
  return /^\s*(?:el\s+)?\d{1,2}\s*$/.test(text)
    || /\b(?:me interesa|me pongo con|arranco con|empiezo con|quiero hacer|voy con)\b/.test(text);
}

export function createOwnerJarvisDayFlowCapability({ ownerApi, dayFlowRef }) {
  return {
    id: "today-live-flow",
    async handle({ raw, text }) {
      const state = dayFlowRef?.current || null;
      const cleanRaw = stripJarvis(raw || text);
      const normalized = normalizeVoiceText(cleanRaw);

      if (state?.kind === "today_pending_selection") {
        if (/\b(?:cancelar|dejalo|otra cosa|ninguno|ninguna)\b/.test(normalized)) {
          dayFlowRef.current = null;
          markDaySelectionActive(false);
          return { handled: true, message: "Listo. No empiezo ningún pendiente." };
        }
        if (isSelectionUtterance(normalized) || /^\d{1,2}$/.test(normalized)) {
          const selected = selectionFromSpeech(cleanRaw, state.candidates || []);
          if (!selected) return { handled: true, message: `No pude ubicar ese pendiente. Elegí un número del 1 al ${state.candidates?.length || 0} o decime parte del nombre.` };
          dayFlowRef.current = null;
          markDaySelectionActive(false);
          const started = await startSession(ownerApi, {
            title: selected.title,
            startAt: new Date(),
            linkedCommitment: selected,
            projectName: selected.projectName,
          });
          if (started?.error) return { handled: true, message: started.error };
          const previous = started?.previous?.session ? ` Cerré ${started.previous.session.title} con ${spokenDuration(started.previous.minutes)} hasta ahora.` : "";
          const label = selected.projectName ? `${selected.projectName}: ${selected.title}` : selected.title;
          return { handled: true, highlightSection: "today-timeline", message: `${previous} Empiezo a contar ${label}, desde las ${formatClock(new Date())}. Cuando pares, decime “terminé” o “paro por ahora”.` };
        }
      }

      if (TODAY_PENDING_RE.test(normalized) && /\b(?:decime|dime|ver|mostra|mostrame|leer|leeme|cuales|que tengo|elegir|hacer)\b/.test(normalized)) {
        const [commitmentsResponse, initiativesResponse] = await Promise.all([
          ownerApi.listCommitments({ status: "open", limit: 200 }),
          ownerApi.listInitiatives({ status: "", limit: 100 }),
        ]);
        const candidates = actionableCommitments(commitmentsResponse?.commitments || [], initiativesResponse?.initiatives || []);
        const visible = candidates.slice(0, 15);
        dayFlowRef.current = visible.length ? { kind: "today_pending_selection", candidates: visible, createdAt: Date.now() } : null;
        markDaySelectionActive(Boolean(visible.length));
        return { handled: true, highlightSection: "today-timeline", message: spokenTodayPendings(candidates) };
      }

      if (ACTIVE_QUERY_RE.test(normalized)) {
        const { session } = await findActiveSession(ownerApi);
        if (!session) return { handled: true, highlightSection: "today-timeline", message: "Ahora no tenés ningún bloque corriendo. Decime “me puse a leer”, “estoy cocinando” o elegí un pendiente y empiezo a contar." };
        const minutes = Math.max(1, Math.round((Date.now() - sessionStartedAt(session).getTime()) / 60000));
        return { handled: true, highlightSection: "today-timeline", message: `Está corriendo ${session.title} desde las ${formatClock(sessionStartedAt(session))}. Llevás aproximadamente ${spokenDuration(minutes)}.` };
      }

      if (FINISH_HINT_RE.test(normalized)) {
        const { session, commitments } = await findActiveSession(ownerApi);
        if (!session) {
          if (POINT_EVENT_RE.test(normalized)) return null;
          return { handled: true, highlightSection: "today-timeline", message: "No encuentro un bloque abierto para cerrar. Si querés registrar algo que ya pasó, decime qué hiciste y a qué hora." };
        }
        const finishAt = hasExplicitClock(cleanRaw) ? dateFromSpeech(cleanRaw) : new Date();
        const completeProjectStep = Boolean(linkedCommitmentId(session) && COMPLETE_PROJECT_RE.test(normalized));
        const closed = await closeSession(ownerApi, session, { finishAt, completeProjectStep, commitments });
        if (closed?.error) return { handled: true, message: closed.error };
        const projectSuffix = closed?.linked
          ? completeProjectStep
            ? " Además marqué ese pendiente como hecho."
            : " El pendiente sigue abierto para que puedas retomarlo después."
          : "";
        return { handled: true, highlightSection: "today-timeline", message: `Cerré ${session.title} a las ${formatClock(finishAt)}. Fueron ${spokenDuration(closed.minutes)}.${projectSuffix}` };
      }

      if (POINT_EVENT_RE.test(normalized)) {
        const occurredAt = dateFromSpeech(cleanRaw);
        const title = momentTitle(normalized);
        const externalKey = `day-moment:${localYmd(occurredAt)}:${occurredAt.getHours()}:${occurredAt.getMinutes()}:${normalizeVoiceText(title)}`;
        const created = await ownerApi.createLifeEntry({
          text: title,
          kind: "event",
          inputSource: "voice",
          occurredAt: occurredAt.toISOString(),
          externalKey,
          structuredData: { jarvis: true, dayMoment: true, sourceText: cleanRaw },
        });
        const entryId = idOf(created?.entry);
        if (entryId) {
          await ownerApi.updateLifeEntry(entryId, {
            status: "resolved",
            resolutionKind: "note",
            resolutionNote: "Momento puntual del día registrado por Jarvis.",
          });
        }
        dispatchMutation();
        return { handled: true, highlightSection: "today-timeline", message: `Anotado: ${title} a las ${formatClock(occurredAt)}.` };
      }

      if (START_HINT_RE.test(normalized)) {
        const title = activityTitle(normalized);
        if (!title) return null;
        const executionContext = activityExecutionContext(title);
        const startAt = hasExplicitClock(cleanRaw) ? dateFromSpeech(cleanRaw) : new Date();

        // Primero registramos la realidad. Entrar en un bloque no puede quedar
        // condicionado a elegir una tarea: el reloj empieza cuando el usuario
        // dice que empezó, aunque todavía no haya decidido qué pendiente atacar.
        const started = await startSession(ownerApi, { title, startAt, executionContext });
        if (started?.error) return { handled: true, message: started.error };
        const previous = started?.previous?.session ? ` Cerré ${started.previous.session.title} con ${spokenDuration(started.previous.minutes)} hasta ese momento.` : "";

        let available = "";
        if (executionContext) {
          try {
            const [commitmentsResponse, initiativesResponse] = await Promise.all([
              ownerApi.listCommitments({ status: "open", limit: 200 }),
              ownerApi.listInitiatives({ status: "", limit: 100 }),
            ]);
            const count = actionableCommitments(commitmentsResponse?.commitments || [], initiativesResponse?.initiatives || [])
              .filter((row) => row.contextKey === executionContext).length;
            if (count) available = ` Tenés ${count} tarea${count === 1 ? "" : "s"} tomada${count === 1 ? "" : "s"} para ${executionContextLabel(executionContext)}, pero no frené el bloque para hacerte elegir.`;
          } catch { /* El bloque vivo no depende de poder listar tareas. */ }
        }
        return { handled: true, highlightSection: "today-timeline", message: `${previous} Listo. ${title} queda corriendo desde las ${formatClock(startAt)}, sin tiempo límite.${available} Cuando cambies de actividad o termines, decímelo y cierro este bloque con la hora real.` };
      }

      return null;
    },
  };
}
